// GrievanceDetails.js - Main component with modal integration
import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import StatusBadge from "../common/StatusBadge";
import Loader from "../common/Loader";

import {
  fetchGrievanceDetails,
  updateGrievanceStatus,
  fetchGrievanceEscalationMatrix,
  fetchApplicantDetails,
} from "../../redux/actions/grievanceActions1";
import "../../styles/GrievanceDetails.css";
import "bootstrap-icons/font/bootstrap-icons.css";

import FilePreviewModal from "../common/FilePreviewModal";
import fileUtils from "../../utils/fileUtils";
import TimeLineItem2 from "../common/TimeLineItem2";
import DocumentCard from "../common/DocumentCard";
import GrievanceDetailsConfig from "../common/GrievanceDetailsConfig";

const EscalatedGrievancesDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { esm, centerName, id } = location.state;
  const [isLoading, setIsLoading] = useState(false);
  const { currentGrievance, loading } = useSelector(
    (state) => state.grievances
  );
  const { user } = useSelector((state) => state.grievances);
  const { applicantDetails } = useSelector((state) => state.grievances);
  const [updateStatus, setUpdateStatus] = useState("");
  const [comments, setComments] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileMetadata, setFileMetadata] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [fileType, setFileType] = useState("");
  const [data, setData] = useState([]);

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);

  useEffect(() => {
    if (id) {
      dispatch(fetchGrievanceDetails(id));
      dispatch(fetchApplicantDetails(esm));
    }

    const fetchMetrices = async () => {
      try {
        const response = await dispatch(fetchGrievanceEscalationMatrix(id));
        if (response) {
          setData(response);
        }
        console.log("matrix details:", response);
      } catch (error) {
        console.error("Error fetching contact:", error);
      }
    };

    fetchMetrices();
  }, [id, dispatch]);

  const resetForm = () => {
    setUpdateStatus("");
    setComments("");
    setFileMetadata([]);
  };

  const handleAcceptGrievance = async () => {
    if (!comments) {
      setErrorMessage("Please add remarks before accepting the grievance");
      return;
    }

    setIsSubmitting(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      // Extract real file objects from file metadata
      const files = fileMetadata.filter((meta) => meta instanceof File);

      let apiAttachments = [];
      if (files.length > 0) {
        apiAttachments = await fileUtils.filesToAttachments(files);
      }

      // Status ID for "Grievance Accepted By Aresa Center & Forwarded"
      const statusId = "4";
      const res = await dispatch(
        updateGrievanceStatus(id, statusId, comments, apiAttachments)
      );

      if (res.rCode == 0) {
        alert(res.rMessage || "Grievance Update Successfully");
       
        navigate("/EscalatedGrievances");
        resetForm();
        await dispatch(fetchGrievanceDetails(id));
      } else {
        alert(res.rMessage || "error");
      }
      // if (res) {
      //   alert("Grievance Update Successfully");
      //   navigate("/EscalatedGrievances");
      //   resetForm();
      //   await dispatch(fetchGrievanceDetails(id));
      // }
      // Reset form
    } catch (error) {
      setErrorMessage(
        error.message || "Failed to accept and forward grievance"
      );
      dispatch(fetchGrievanceDetails(id));
    } finally {
      setIsSubmitting(false);
    }
  };

  const closePreviewModal = () => {
    console.log("Closing modal");
    setIsPreviewModalOpen(false);
    setPreviewAttachment(null);
  };

  if (loading || !currentGrievance) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-grow text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  const handleTimelineAttachmentPreview = (state, attachment, name) => {
    setIsLoading(state);
    setPreviewAttachment(attachment);
    setFileType(name);
    if (!state) {
      setIsPreviewModalOpen(true);
    }
  };

  const getAllAttachments = () => {
    const attachments = [];
    if (currentGrievance?.updates) {
      for (const update of currentGrievance.updates) {
        if (update?.attachments && update.attachments.length > 0) {
          attachments.push(...update.attachments);
        }
      }
    }
    return attachments;
  };

  const allAttachments = getAllAttachments();

  const currentStatusId =
    currentGrievance?.current_status_id?.toString() || "1";

  

  return (
    <div className="grievance-details-container">
      <FilePreviewModal
        isOpen={isPreviewModalOpen}
        onClose={closePreviewModal}
        attachment={previewAttachment}
        fileTypes={fileType}
      />

      {/* Header with grievance info */}
      <div className="grievance-details-header">
        <div style={{ display: "flex", flexDirection: "row", gap: 30 }}>
          <button
            className="fab-button"
            onClick={() => navigate("/EscalatedGrievances")}
            title="Back to grants"
          >
            <i className="bi bi-arrow-left"></i>
          </button>
          <div className="grievance-details-title">
            <div className="title-icon">
              <i className="bi bi-clipboard-check"></i>
            </div>
            <div>
              <h2>G{currentGrievance?.grv_id?.toString().padStart(10, "0")}</h2>
              <p className="grievance-subtitle">
                {currentGrievance?.grievance_details}
              </p>
            </div>
          </div>
        </div>

        <StatusBadge
          status={GrievanceDetailsConfig.getStatusName(
            currentGrievance.current_status_id
          )}
          size="large"
          color="#000"
        />
          {/* <StatusBadge
                  status={grievanceConfig.getStatusName(currentStatusId)}
                  size="large"
                  color="#000"
                /> */}
      </div>

      <div className="grievance-details-tabs">
        <button
          className={`tab-button ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          <i className="bi bi-info-circle"></i> Overview
        </button>
        <button
          className={`tab-button ${activeTab === "timeline" ? "active" : ""}`}
          onClick={() => setActiveTab("timeline")}
        >
          <i className="bi bi-clock-history"></i> Status Trail
          <span className="tab-badge">{allAttachments.length}</span>
        </button>
        <button
          className={`tab-button ${activeTab === "documents" ? "active" : ""}`}
          onClick={() => setActiveTab("documents")}
        >
          <i className="bi bi-file-earmark"></i> Documents
          <span className="tab-badge">{allAttachments.length}</span>
        </button>
        <button
          className={`tab-button ${activeTab === "update" ? "active" : ""}`}
          onClick={() => setActiveTab("update")}
        >
          <i className="bi bi-pencil-square"></i> Update Progress
        </button>
      </div>

      {/* Tab content */}
      <div className="grievance-details-content">
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="grievance-details-panel">
            <div className="grievance-details-card applicant-card">
              <div className="card-header">
                <h3>
                  <i className="bi bi-person"></i> Applicant Information
                </h3>
                <div className="card-avatar">
                  <span>{applicantDetails?.USER_NAME?.charAt(0) || "U"}</span>
                </div>
              </div>
              <div className="card-body">
                <div className="applicant-name">
                  {applicantDetails?.USER_NAME}
                </div>

                <div className="info-grid">
                  <div className="info-item">
                    <div className="info-icon">
                      <i className="bi bi-calendar2"></i>
                    </div>
                    <div>
                      <div className="info-label">Submission Date</div>
                      <div className="info-value">
                        {applicantDetails?.SOS_DATE || "N/A"}
                      </div>
                    </div>
                  </div>

                  <div className="info-item">
                    <div className="info-icon">
                      <i className="bi bi-building"></i>
                    </div>
                    <div>
                      <div className="info-label">ARESA Center</div>
                      <div className="info-value">{centerName || "N/A"}</div>
                    </div>
                  </div>

                  {applicantDetails?.SERVICE_NO && (
                    <div className="info-item">
                      <div className="info-icon">
                        <i className="bi bi-card-heading"></i>
                      </div>
                      <div>
                        <div className="info-label">Service No</div>
                        <div className="info-value">
                          {applicantDetails.SERVICE_NO}
                        </div>
                      </div>
                    </div>
                  )}

                  {applicantDetails?.RANK_NAME && (
                    <div className="info-item">
                      <div className="info-icon">
                        <i className="bi bi-star"></i>
                      </div>
                      <div>
                        <div className="info-label">Rank</div>
                        <div className="info-value">
                          {applicantDetails.RANK_NAME}
                        </div>
                      </div>
                    </div>
                  )}

                  {applicantDetails?.SOS_DATE && (
                    <div className="info-item">
                      <div className="info-icon">
                        <i className="bi bi-calendar-event"></i>
                      </div>
                      <div>
                        <div className="info-label">SOS Date</div>
                        <div className="info-value">
                          {applicantDetails.SOS_DATE}
                        </div>
                      </div>
                    </div>
                  )}

                  {currentGrievance?.ppoNo && (
                    <div className="info-item">
                      <div className="info-icon">
                        <i className="bi bi-file-earmark-text"></i>
                      </div>
                      <div>
                        <div className="info-label">PPO No</div>
                        <div className="info-value">
                          {currentGrievance.ppoNo}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="info-item">
                    <div className="info-icon">
                      <i className="bi bi-file-earmark-text"></i>
                    </div>
                    <div>
                      <div className="info-label">Section Name</div>
                      <div className="info-value">
                        {currentGrievance?.updates[0]?.SECTION_NAME || "N/A"}
                      </div>
                    </div>
                  </div>
                  <div className="info-item">
                    <div className="info-icon">
                      <i className="bi bi-file-earmark-text"></i>
                    </div>
                    <div>
                      <div className="info-label">Branch Name</div>
                      <div className="info-value">
                        {currentGrievance?.updates[0]?.BRANCH_NAME || "N/A"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grievance-details-card description-card">
              <div className="card-header">
                <h3>
                  <i className="bi bi-chat-left-text"></i> Grievance Details
                </h3>
              </div>
              <div className="card-body">
                <div className="description-box">
                  <div className="description-title">
                    <i className="bi bi-card-text"></i> Description
                  </div>
                  <div className="description-content">
                    {currentGrievance?.grievance_details}
                  </div>
                </div>

                <div className="status-action-section">
                  <div className="current-status">
                    <p className="status-label">Current Status</p>
                    <StatusBadge
                      status={GrievanceDetailsConfig.getStatusName(
                        currentGrievance.current_status_id
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Timeline Tab */}
        {activeTab === "timeline" && (
          <div className="grievance-details-panel full-width">
            <div className="grievance-details-card">
              <div className="card-header">
                <h3>
                  <i className="bi bi-clock-history"></i> Grievance Timeline
                </h3>
              </div>

              <div className="card-body">
                {currentGrievance?.updates?.length > 0 ? (
                  <div className="timeline-container">
                    {currentGrievance.updates.map((entry, index) => (
                      <TimeLineItem2
                        key={index}
                        action={entry.remarks}
                        status_name={entry.status_name}
                        date={entry.entry_datetime}
                        comments={entry.remarks}
                        attachments={entry.attachments}
                        user_name={entry.user_name}
                        onPreviewAttachment={handleTimelineAttachmentPreview}
                        isLast={index === currentGrievance.updates.length - 1}
                        isFirst={index === 0}
                        isLoading={isLoading}
                        type="grievance"
                      />
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <div className="empty-icon">
                      <i className="bi bi-clock-history"></i>
                    </div>
                    <p>No timeline events available for this grievance yet.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === "documents" && (
          <div className="grievance-details-panel full-width">
            <div className="grievance-details-card">
              <div className="card-header">
                <h3>
                  <i className="bi bi-files"></i> Attached Documents
                </h3>
              </div>
              <div className="card-body">
                {allAttachments?.length > 0 ? (
                  <>
                    <ul className="document-categories">
                      <li className="category-active">All Documents</li>
                    </ul>

                    <div className="documents-grid">
                      {allAttachments.map((attachment, index) => (
                        <DocumentCard
                          filename={attachment.att_name}
                          attachmentId={attachment.grv_att_id} // grant_att_id, grv_att_id, or idcard_att_id
                          type="grievance" // or whatever type needed for the API
                          onPreview={handleTimelineAttachmentPreview}
                          isLoading={isLoading}
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="empty-state">
                    <div className="empty-icon">
                      <i className="bi bi-file-earmark-x"></i>
                    </div>
                    <p>No documents attached to this grievance</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Update Status Tab */}
        {activeTab === "update" && (
          <div className="grievance-details-panel full-width">
            <div className="grievance-details-card">
              <div className="card-header">
                <h3>
                  <i className="bi bi-pencil-square"></i> Update Status
                </h3>
              </div>
              <div className="card-body">
                <div className="form-group">
                  <label htmlFor="comments">
                    <i className="bi bi-chat-text"></i> Remarks
                  </label>
                  <textarea
                    id="comments"
                    className="form-control"
                    rows="4"
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    placeholder="Add your remarks or notes"
                  ></textarea>
                </div>

                <div className="aresa-form-actions">
                  <button
                    className="aresa-button-secondary"
                    onClick={resetForm}
                    disabled={isSubmitting}
                    type="button"
                  >
                    <i className="bi bi-x-circle"></i> Cancel
                  </button>

                  <button
                    className="aresa-button-success"
                    onClick={handleAcceptGrievance}
                    disabled={isSubmitting || !comments}
                    type="button"
                  >
                    <i className="bi bi-check-circle"></i> Update Status
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {isLoading && <Loader></Loader>}
    </div>
  );
};

export default EscalatedGrievancesDetails;
